import React, { createContext, useContext, useState, useCallback } from 'react';
import { useUserPaymentCanisterContext } from '../Hooks/userPaymentCanisterProvider';

// Create Products Context
const ProductsContext = createContext(null);

// Custom hook to use Products context
export const useProductsContext = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProductsContext must be used within a ProductsProvider');
  }
  return context;
};

// Products Provider Component
export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { userPaymentService } = useUserPaymentCanisterContext();

  const fetchProduct = useCallback(async (productId) => {
    if (!userPaymentService || !userPaymentService.actor) {
      return null;
    }

    setLoading(true);
    setError(null);
    console.log("product id", productId);
    
    try {
      const result = await userPaymentService.actor.get_product(productId);
      console.log("result in fetchProduct by id", result);
      
      if ('Ok' in result) {
        // Transform product data from Candid format to frontend format
        const product = transformProductFromCandid(result.Ok);
        return product;
      } else {
        const errorMsg = result.Err || 'Failed to fetch product';
        setError(errorMsg);
        console.error('Failed to fetch product:', errorMsg);
        return null;
      }
    } catch (err) {
      const errorMsg = err.message || 'Unknown error occurred';
      setError(errorMsg);
      console.error('Error fetching product:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userPaymentService]);

  const fetchProducts = useCallback(async () => {
    if (!userPaymentService || !userPaymentService.actor) {
      return [];
    }

    setLoading(true);
    setError(null);
    
    try {
      const rawProducts = await userPaymentService.actor.list_active_products();
      const transformedProducts = rawProducts.map(transformProductFromCandid);
      setProducts(transformedProducts);
      return transformedProducts;
    } catch (err) {
      const errorMsg = err.message || 'Unknown error occurred';
      setError(errorMsg);
      console.error('Error fetching products:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userPaymentService]);

  const createInvoiceForProduct = useCallback(async (productId, quantity = 1, metadata = []) => {
    if (!userPaymentService || !userPaymentService.actor) {
      throw new Error('User payment service not available');
    }

    try {
      const result = await userPaymentService.actor.create_invoice_for_product(
        productId,
        quantity,
        metadata
      );
      
      if ('Ok' in result) {
        return result.Ok;
      } else {
        throw new Error(result.Err || 'Failed to create invoice');
      }
    } catch (err) {
      console.error('Error creating product invoice:', err);
      throw err;
    }
  }, [userPaymentService]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const contextValue = {
    // State
    products,
    loading,
    error,
    
    // Actions
    fetchProduct,
    fetchProducts,
    createInvoiceForProduct,
    clearError,
  };

  return (
    <ProductsContext.Provider value={contextValue}>
      {children}
    </ProductsContext.Provider>
  );
};

// Helper function to transform product data from Candid format
const transformProductFromCandid = (candidProduct) => {
  const transformStatus = (status) => {
    if (typeof status === 'object' && status !== null) {
      if ('Active' in status) return 'Active';
      if ('Inactive' in status) return 'Inactive';
      if ('OutOfStock' in status) return 'OutOfStock';
    }
    return status;
  };

  const extractOptional = (optional) => {
    if (Array.isArray(optional) && optional.length > 0) {
      return optional[0];
    }
    return null;
  };

  return {
    id: candidProduct.product_id || candidProduct.id,
    name: candidProduct.name,
    description: candidProduct.description,
    price: Number(candidProduct.price) / 1e8, // Convert from e8s to decimal
    token_symbol: candidProduct.token_symbol,
    category: extractOptional(candidProduct.category),
    status: transformStatus(candidProduct.status),
    inventory_count: extractOptional(candidProduct.inventory_count),
    image_url: extractOptional(candidProduct.image_url),
    metadata: candidProduct.metadata || [],
    created_at: Number(candidProduct.created_at),
    updated_at: Number(candidProduct.updated_at),
  };
};

export default ProductsProvider;
